import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import SolverGrid from '../components/solver/SolverGrid'
import CluePanel from '../components/solver/CluePanel'
import Timer from '../components/solver/Timer'
import HintsToolbar from '../components/solver/HintsToolbar'
import NameModal from '../components/solver/NameModal'
import CompletionModal from '../components/solver/CompletionModal'
import useTimer from '../hooks/useTimer'
import useKeyboardNavigation from '../hooks/useKeyboardNavigation'
import { calculateNumberedCells, copyGrid, getWordAtCell, getClueNumber } from '../utils/gridUtils'
import { puzzleApi, solveApi } from '../utils/api'

const styles = {
  container: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  gridArea: {
    flex: '0 0 auto',
    minWidth: '320px',
  },
  cluesArea: {
    flex: '1 1 400px',
    minWidth: '400px',
    height: '600px',
  },
  activeClueBar: {
    background: 'var(--color-warning)',
    padding: '12px 16px',
    marginBottom: '16px',
    fontWeight: '600',
    minHeight: '24px',
  },
  activeClueNumber: {
    fontWeight: '700',
    marginRight: '8px',
  },
  activeClueDirection: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'var(--color-muted)',
    marginLeft: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '1.5rem',
  },
  meta: {
    fontSize: '0.875rem',
    color: 'var(--color-muted)',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  link: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    fontWeight: '600',
    textDecoration: 'none',
  },
  resetButton: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    fontWeight: '600',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 'inherit',
    fontFamily: 'inherit',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--color-muted)',
  },
  error: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--color-error)',
  },
}

// Helper to get localStorage key for a puzzle
const getStorageKey = (puzzleId) => `puzzle-solve-${puzzleId}`

export default function SolvePuzzle() {
  const { id } = useParams()

  // Puzzle data
  const [puzzle, setPuzzle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Solve state
  const [solveId, setSolveId] = useState(null)
  const [solverName, setSolverName] = useState(null)
  const [showNameModal, setShowNameModal] = useState(true)
  const [showCompletionModal, setShowCompletionModal] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  // User grid (what the user has entered)
  const [userGrid, setUserGrid] = useState(null)

  // Navigation state
  const [selectedCell, setSelectedCell] = useState(null)
  const [direction, setDirection] = useState('across')

  // Hints state
  const [hintsUsed, setHintsUsed] = useState(0)
  const [cellStates, setCellStates] = useState({})

  // Timer
  const timer = useTimer(0)

  // Auto-save ref
  const autoSaveRef = useRef(null)

  // Track if we restored from localStorage
  const restoredRef = useRef(false)

  // Fetch puzzle on mount
  useEffect(() => {
    async function fetchPuzzle() {
      try {
        const data = await puzzleApi.get(id)
        setPuzzle(data)

        // Check for saved state in localStorage
        const savedState = localStorage.getItem(getStorageKey(id))
        if (savedState) {
          try {
            const parsed = JSON.parse(savedState)
            if (parsed.userGrid) {
              setUserGrid(parsed.userGrid)
              restoredRef.current = true
            }
            if (parsed.solveId) setSolveId(parsed.solveId)
            if (parsed.solverName) setSolverName(parsed.solverName)
            if (parsed.hintsUsed) setHintsUsed(parsed.hintsUsed)
            if (parsed.cellStates) {
              // Only restore 'revealed' states, not temporary check states
              const revealedStates = {}
              Object.entries(parsed.cellStates).forEach(([key, value]) => {
                if (value === 'revealed') revealedStates[key] = value
              })
              setCellStates(revealedStates)
            }
            if (parsed.elapsedTime) timer.setTime(parsed.elapsedTime)
            // Skip name modal if we have saved state
            setShowNameModal(false)
            // Restore completed state
            if (parsed.isCompleted) {
              setIsCompleted(true)
            } else {
              // Start timer automatically when restoring (only if not completed)
              setTimeout(() => timer.start(), 100)
            }
          } catch (e) {
            console.error('Failed to restore saved state:', e)
          }
        }

        // Initialize user grid (empty) only if not restored
        if (!restoredRef.current) {
          const emptyGrid = data.grid.map(row =>
            row.map(cell => ({
              letter: '',
              isBlack: cell.isBlack,
            }))
          )
          setUserGrid(emptyGrid)
        }

        // Set initial selected cell to first non-black cell
        for (let r = 0; r < data.grid.length; r++) {
          for (let c = 0; c < data.grid[r].length; c++) {
            if (!data.grid[r][c].isBlack) {
              setSelectedCell({ row: r, col: c })
              break
            }
          }
          if (selectedCell) break
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchPuzzle()
  }, [id])

  // Numbered cells
  const numberedCells = useMemo(() => {
    if (!puzzle?.grid) return { cells: {}, acrossEntries: [], downEntries: [] }
    return calculateNumberedCells(puzzle.grid)
  }, [puzzle?.grid])

  // Active clue
  const activeClue = useMemo(() => {
    if (!selectedCell || !puzzle?.grid || puzzle.grid[selectedCell.row][selectedCell.col].isBlack) {
      return null
    }
    const clueNum = getClueNumber(numberedCells, puzzle.grid, selectedCell.row, selectedCell.col, direction)
    if (!clueNum) return null
    const clueText = puzzle.clues?.[direction]?.[clueNum] || ''
    return { num: clueNum, text: clueText, direction }
  }, [selectedCell, puzzle?.grid, puzzle?.clues, direction, numberedCells])

  // Start solve
  const handleStartSolve = async (name) => {
    setShowNameModal(false)
    setSolverName(name)

    try {
      const result = await solveApi.start(id, name)
      setSolveId(result.id)
    } catch (err) {
      console.error('Failed to start solve:', err)
    }

    timer.start()
  }

  // Reset puzzle to start fresh
  const handleReset = () => {
    if (!confirm('Are you sure you want to reset? All progress will be lost.')) {
      return
    }

    // Clear localStorage
    localStorage.removeItem(getStorageKey(id))

    // Reset all state
    setSolveId(null)
    setSolverName(null)
    setIsCompleted(false)
    setShowCompletionModal(false)
    setHintsUsed(0)
    setCellStates({})
    timer.reset()

    // Reset user grid to empty
    if (puzzle?.grid) {
      const emptyGrid = puzzle.grid.map(row =>
        row.map(cell => ({
          letter: '',
          isBlack: cell.isBlack,
        }))
      )
      setUserGrid(emptyGrid)
    }

    // Show name modal to start fresh
    setShowNameModal(true)
  }

  // Handle letter input
  const handleLetterInput = useCallback((row, col, letter) => {
    setUserGrid(g => {
      const newGrid = copyGrid(g)
      newGrid[row][col].letter = letter
      return newGrid
    })

    // Clear cell state on edit
    setCellStates(s => {
      const key = `${row}-${col}`
      if (s[key]) {
        const newStates = { ...s }
        delete newStates[key]
        return newStates
      }
      return s
    })
  }, [])

  // Handle delete
  const handleDelete = useCallback((row, col) => {
    setUserGrid(g => {
      const newGrid = copyGrid(g)
      newGrid[row][col].letter = ''
      return newGrid
    })
  }, [])

  // Keyboard navigation
  const { handleKeyDown } = useKeyboardNavigation({
    grid: puzzle?.grid,
    userGrid,
    selectedCell,
    setSelectedCell,
    direction,
    setDirection,
    onLetterInput: handleLetterInput,
    onDelete: handleDelete,
    numberedCells,
  })

  // Handle clue click
  const handleClueClick = (row, col, dir) => {
    setSelectedCell({ row, col })
    setDirection(dir)
  }

  // Check if puzzle is complete
  useEffect(() => {
    if (!userGrid || !puzzle?.grid || isCompleted) return

    // Check if all cells are filled
    let allFilled = true
    for (let r = 0; r < userGrid.length; r++) {
      for (let c = 0; c < userGrid[r].length; c++) {
        if (!userGrid[r][c].isBlack && !userGrid[r][c].letter) {
          allFilled = false
          break
        }
      }
      if (!allFilled) break
    }

    if (allFilled) {
      // Check answers against solution
      checkCompletion()
    }
  }, [userGrid])

  // Check completion
  const checkCompletion = async () => {
    if (!userGrid || !puzzle?.grid) return

    try {
      // Send all cells to server for checking
      const cells = []
      for (let r = 0; r < userGrid.length; r++) {
        for (let c = 0; c < userGrid[r].length; c++) {
          if (!userGrid[r][c].isBlack && userGrid[r][c].letter) {
            cells.push({ row: r, col: c, letter: userGrid[r][c].letter })
          }
        }
      }

      const result = await puzzleApi.check(id, cells)

      if (result.allCorrect) {
        timer.pause()
        setIsCompleted(true)

        // Submit completion
        if (solveId) {
          await solveApi.complete(solveId, timer.seconds)
        }

        setShowCompletionModal(true)
      }
    } catch (err) {
      console.error('Check failed:', err)
    }
  }

  // Hint handlers
  const handleCheckCell = async () => {
    if (!selectedCell) return

    try {
      const cells = [{
        row: selectedCell.row,
        col: selectedCell.col,
        letter: userGrid[selectedCell.row][selectedCell.col].letter
      }]
      const result = await puzzleApi.check(id, cells)

      const key = `${selectedCell.row}-${selectedCell.col}`
      setCellStates(s => ({
        ...s,
        [key]: result.results[0]?.correct ? 'correct' : 'incorrect'
      }))

      // Clear state after 2 seconds
      setTimeout(() => {
        setCellStates(s => {
          const newStates = { ...s }
          delete newStates[key]
          return newStates
        })
      }, 2000)

      setHintsUsed(h => h + 1)
    } catch (err) {
      console.error('Check cell failed:', err)
    }
  }

  const handleCheckWord = async () => {
    if (!selectedCell || !puzzle?.grid) return

    const word = getWordAtCell(puzzle.grid, selectedCell.row, selectedCell.col, direction)
    if (!word) return

    try {
      const cells = word.cells.map(c => ({
        row: c.row,
        col: c.col,
        letter: userGrid[c.row][c.col].letter
      }))

      const result = await puzzleApi.check(id, cells)

      const newStates = { ...cellStates }
      result.results.forEach((r, i) => {
        const key = `${cells[i].row}-${cells[i].col}`
        newStates[key] = r.correct ? 'correct' : 'incorrect'
      })
      setCellStates(newStates)

      // Clear states after 2 seconds
      setTimeout(() => {
        setCellStates(s => {
          const newStates = { ...s }
          word.cells.forEach(c => {
            delete newStates[`${c.row}-${c.col}`]
          })
          return newStates
        })
      }, 2000)

      setHintsUsed(h => h + 1)
    } catch (err) {
      console.error('Check word failed:', err)
    }
  }

  const handleRevealCell = async () => {
    if (!selectedCell) return

    try {
      const result = await puzzleApi.reveal(id, 'cell', selectedCell.row, selectedCell.col, direction)

      if (result.letters && result.letters.length > 0) {
        const letter = result.letters[0]
        setUserGrid(g => {
          const newGrid = copyGrid(g)
          newGrid[letter.row][letter.col].letter = letter.letter
          return newGrid
        })

        const key = `${letter.row}-${letter.col}`
        setCellStates(s => ({ ...s, [key]: 'revealed' }))
      }

      setHintsUsed(h => h + 1)
    } catch (err) {
      console.error('Reveal cell failed:', err)
    }
  }

  const handleRevealWord = async () => {
    if (!selectedCell || !puzzle?.grid) return

    try {
      const result = await puzzleApi.reveal(id, 'word', selectedCell.row, selectedCell.col, direction)

      if (result.letters && result.letters.length > 0) {
        setUserGrid(g => {
          const newGrid = copyGrid(g)
          result.letters.forEach(l => {
            newGrid[l.row][l.col].letter = l.letter
          })
          return newGrid
        })

        const newStates = { ...cellStates }
        result.letters.forEach(l => {
          newStates[`${l.row}-${l.col}`] = 'revealed'
        })
        setCellStates(newStates)
      }

      setHintsUsed(h => h + 1)
    } catch (err) {
      console.error('Reveal word failed:', err)
    }
  }

  const handleCheckPuzzle = async () => {
    if (!userGrid || !puzzle?.grid) return

    try {
      // Collect all filled cells
      const cells = []
      for (let r = 0; r < userGrid.length; r++) {
        for (let c = 0; c < userGrid[r].length; c++) {
          if (!userGrid[r][c].isBlack && userGrid[r][c].letter) {
            cells.push({ row: r, col: c, letter: userGrid[r][c].letter })
          }
        }
      }

      if (cells.length === 0) {
        alert('No cells filled yet!')
        return
      }

      const result = await puzzleApi.check(id, cells)

      // Mark incorrect cells
      const newStates = { ...cellStates }
      let incorrectCount = 0
      result.results.forEach(r => {
        const key = `${r.row}-${r.col}`
        if (!r.correct) {
          newStates[key] = 'incorrect'
          incorrectCount++
        }
      })
      setCellStates(newStates)

      // Check if all filled and all correct
      const totalNonBlackCells = puzzle.grid.flat().filter(c => !c.isBlack).length
      if (result.allCorrect && cells.length === totalNonBlackCells) {
        // Trigger completion
        timer.pause()
        setIsCompleted(true)
        if (solveId) {
          await solveApi.complete(solveId, timer.seconds)
        }
        setShowCompletionModal(true)
      } else if (incorrectCount > 0) {
        alert(`${incorrectCount} cell${incorrectCount !== 1 ? 's' : ''} incorrect`)
        // Clear incorrect highlighting after 3 seconds
        setTimeout(() => {
          setCellStates(s => {
            const cleared = { ...s }
            result.results.forEach(r => {
              if (!r.correct) {
                delete cleared[`${r.row}-${r.col}`]
              }
            })
            return cleared
          })
        }, 3000)
      } else {
        alert('All filled cells are correct!')
      }

      setHintsUsed(h => h + 1)
    } catch (err) {
      console.error('Check puzzle failed:', err)
    }
  }

  // Auto-save progress every 10 seconds
  useEffect(() => {
    if (!solveId || !userGrid || isCompleted) return

    autoSaveRef.current = setInterval(async () => {
      try {
        await solveApi.update(solveId, {
          progressData: JSON.stringify(userGrid),
          solveTime: timer.seconds,
          hintsUsed,
        })
      } catch (err) {
        console.error('Auto-save failed:', err)
      }
    }, 10000)

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current)
      }
    }
  }, [solveId, userGrid, timer.seconds, hintsUsed, isCompleted])

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (!userGrid || showNameModal) return

    const stateToSave = {
      userGrid,
      solveId,
      solverName,
      hintsUsed,
      cellStates,
      elapsedTime: timer.seconds,
      isCompleted,
    }
    localStorage.setItem(getStorageKey(id), JSON.stringify(stateToSave))
  }, [userGrid, solveId, solverName, hintsUsed, cellStates, timer.seconds, isCompleted, showNameModal, id])

  // Warn user before leaving page with unsaved progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only warn if puzzle is in progress (not completed, not at name modal)
      if (!showNameModal && !isCompleted && userGrid) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [showNameModal, isCompleted, userGrid])

  if (loading) {
    return <div style={styles.loading}>Loading puzzle...</div>
  }

  if (error) {
    return (
      <div style={styles.error}>
        <p>Failed to load puzzle: {error}</p>
        <Link to="/" style={{ color: 'var(--color-accent)' }}>Back to Home</Link>
      </div>
    )
  }

  if (!puzzle) {
    return (
      <div style={styles.error}>
        <p>Puzzle not found</p>
        <Link to="/" style={{ color: 'var(--color-accent)' }}>Back to Home</Link>
      </div>
    )
  }

  return (
    <div>
      {showNameModal && (
        <NameModal
          puzzleTheme={puzzle.theme}
          onStart={handleStartSolve}
        />
      )}

      {showCompletionModal && (
        <CompletionModal
          solveTime={timer.seconds}
          hintsUsed={hintsUsed}
          puzzleId={id}
          formatTime={timer.formatTime}
          solverName={solverName}
        />
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{puzzle.theme || 'Crossword Puzzle'}</h1>
          {puzzle.creatorName && (
            <p style={styles.meta}>By {puzzle.creatorName}</p>
          )}
        </div>
        <div style={styles.actions}>
          <Timer
            seconds={timer.seconds}
            isRunning={timer.isRunning}
            onToggle={timer.toggle}
            formatTime={timer.formatTime}
          />
          <Link to={`/puzzle/${id}/leaderboard`} style={styles.link}>
            Leaderboard
          </Link>
          <Link to={`/puzzle/${id}/print`} style={styles.link}>
            Print
          </Link>
          <button onClick={handleReset} style={styles.resetButton}>
            Reset
          </button>
        </div>
      </div>

      <HintsToolbar
        onCheckCell={handleCheckCell}
        onCheckWord={handleCheckWord}
        onCheckPuzzle={handleCheckPuzzle}
        onRevealCell={handleRevealCell}
        onRevealWord={handleRevealWord}
        hintsUsed={hintsUsed}
        disabled={isCompleted}
      />

      {/* Active clue bar above the puzzle */}
      <div style={styles.activeClueBar}>
        {activeClue ? (
          <>
            <span style={styles.activeClueNumber}>{activeClue.num}{activeClue.direction === 'across' ? 'A' : 'D'}</span>
            {activeClue.text || <em style={{ color: 'var(--color-muted)' }}>No clue</em>}
          </>
        ) : (
          <em style={{ color: 'var(--color-muted)' }}>Select a cell to see the clue</em>
        )}
      </div>

      <div style={styles.container}>
        <div style={styles.gridArea}>
          <SolverGrid
            grid={puzzle.grid}
            userGrid={userGrid}
            numberedCells={numberedCells}
            selectedCell={selectedCell}
            setSelectedCell={setSelectedCell}
            direction={direction}
            setDirection={setDirection}
            onKeyDown={handleKeyDown}
            cellStates={cellStates}
            isPaused={!timer.isRunning && !showNameModal && !isCompleted}
          />
        </div>

        <div style={styles.cluesArea}>
          <CluePanel
            grid={puzzle.grid}
            userGrid={userGrid}
            numberedCells={numberedCells}
            clues={puzzle.clues || { across: {}, down: {} }}
            selectedCell={selectedCell}
            direction={direction}
            onClueClick={handleClueClick}
            isPaused={!timer.isRunning && !showNameModal && !isCompleted}
          />
        </div>
      </div>
    </div>
  )
}
